import React from 'react';
import Price from '../price';
import { TilaProduct } from './tila-modal';

interface TilaReceiptProps {
  product: TilaProduct | undefined;
}

const TilaReceipt: React.FC<TilaReceiptProps> = ({ product }) => {
  if(!product) return null;
  
  for (const key in product.metafields) {
    if (Object.hasOwn(product.metafields, key)) {
      product.metafields[key] = escapeQuotes(product.metafields[key]);
    }
  }

  const {
    'payment-type': paymentType,
    'num-remaining-payments': numRemainingPayments,
    'additional-payments-amount': additionalPaymentsAmount,
    'annual-percent-rate': annualPercentRate,
    'finance-charge': financeCharge,
    'amount-financed': amountFinanced,
    'total-of-payments': totalOfPayments,
    'total-sales-price': totalSalesPrice,
  } = product.metafields;

  const totalPayments = parseFloat(numRemainingPayments) + 1;

  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today.setMonth(today.getMonth() + parseFloat(numRemainingPayments)));

  const startDateString = startDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const endDateString = endDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <h5>Payment Schedule:</h5>
      <p>
        I agree to make {totalPayments} monthly payments of <Price amount={additionalPaymentsAmount} currencyCode='USD'/>, starting{' '}
        {startDateString} and ending {endDateString}, unless modified by an off-schedule or partial payment. If a manual or off-schedule partial payment is made, my remaining balance will be recalculated. This may result in a modified final payment amount or an additional payment to cover the remaining balance. The total amount due will not increase because of an off-schedule payment.
      </p>

      <h5>Prepayment Option:</h5>
      <p>
        At any time in the next {totalPayments} months, I may contact 4Patriots to pay the discounted &apos;full pay&apos; sales price, with credit applied for any prior payments. This allows me to receive 0% APR (same as cash). If I have made partial payments, my prepayment amount will be adjusted accordingly based on my remaining balance at the time of prepayment. Contact <a href="tel:1-800-798-3351">800-798-3351</a> or <a href="mailto:paymentplans@4patriots.com">paymentplans@4patriots.com</a> to request a prepayment.
      </p>

      <div className="tila-modal-main-bottom-part-table">
        <div className="tila-modal-main-bottom-part-table-column">
          <div className="tila-modal-main-bottom-part-table-column-header">Annual % Rate*</div>
          <div className="tila-modal-main-bottom-part-table-column-info">
            The cost of your credit as a yearly rate.
          </div>
          <div className="tila-modal-main-bottom-part-table-column-footer">
            {annualPercentRate}%
          </div>
        </div>

        <div className="tila-modal-main-bottom-part-table-column">
          <div className="tila-modal-main-bottom-part-table-column-header">Finance Charge</div>
          <div className="tila-modal-main-bottom-part-table-column-info">
            The dollar amount the credit will cost you.
          </div>
          <div className="tila-modal-main-bottom-part-table-column-footer">
            <Price amount={financeCharge} currencyCode='USD'/>
          </div>
        </div>

        <div className="tila-modal-main-bottom-part-table-column">
          <div className="tila-modal-main-bottom-part-table-column-header">Amount Financed</div>
          <div className="tila-modal-main-bottom-part-table-column-info">
            The amount of credit provided to you or on your behalf.
          </div>
          <div className="tila-modal-main-bottom-part-table-column-footer">
            <Price amount={amountFinanced} currencyCode='USD'/>
          </div>
        </div>

        <div className="tila-modal-main-bottom-part-table-column">
          <div className="tila-modal-main-bottom-part-table-column-header">Total of Payments</div>
          <div className="tila-modal-main-bottom-part-table-column-info">
            The amount you will have paid after you have made all payments as scheduled.
          </div>
          <div className="tila-modal-main-bottom-part-table-column-footer">
            <Price amount={totalOfPayments} currencyCode='USD'/>
          </div>
        </div>

        <div className="tila-modal-main-bottom-part-table-column">
          <div className="tila-modal-main-bottom-part-table-column-header">Total Sales Price</div>
          <div className="tila-modal-main-bottom-part-table-column-info">
            The total cost of your purchase on credit, including your down payment of <Price amount={additionalPaymentsAmount} currencyCode='USD'/>.
          </div>
          <div className="tila-modal-main-bottom-part-table-column-footer">
            <Price amount={totalSalesPrice} currencyCode='USD'/>
          </div>
        </div>
      </div>
    </>
  );
};

export default TilaReceipt;

const escapeQuotes = (str: any) => {
  if (typeof str === 'string' && str.startsWith('"') && str.endsWith('"')) {
    return str.substring(1, str.length - 1);
  }
  return str;
};
